import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const request = async () => {
      const abortController = this.#createAbortController();

      try {
        await window.BionoboCartMutations?.prepareMutation?.();
        const existingDiscounts = this.#existingDiscounts();
        if (existingDiscounts.includes(discountCodeValue)) return;

        cartDiscountError.classList.add('hidden');
        cartDiscountErrorDiscountCode.classList.add('hidden');
        cartDiscountErrorShipping.classList.add('hidden');

        const config = fetchConfig('json', {
          body: JSON.stringify({
            discount: [...existingDiscounts, discountCodeValue].join(','),
            sections: window.BionoboCartMutations?.createSectionRequest?.().ids || [this.dataset.sectionId],
            sections_url: window.location.pathname,
          }),
        });

        const response = await fetch(Theme.routes.cart_update_url, {
          ...config,
          signal: abortController.signal,
        });

        let data = await response.json();
        if (!response.ok || data.status || data.errors) {
          const error = new Error(data.description || data.message || 'The discount could not be updated.');
          error.cartMutationUncertain = response.status !== 422;
          throw error;
        }
        data = await this.#reconcileCartUpdate(data);

        if (
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === false;
          })
        ) {
          discountCode.value = '';
          await this.#publishCartUpdate(data);
          this.#handleDiscountError('discount_code');
          return;
        }

        const newHtml = data.sections[this.dataset.sectionId];
        const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
        const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
        const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
        if (section) {
          const codes = Array.from(discountCodes)
            .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
            .filter(Boolean);
          // Before morphing, we need to check if the shipping discount is applicable in the UI
          // we check the liquid logic compared to the cart payload to assess whether we leveraged
          // a valid shipping discount code.
          if (
            codes.length === existingDiscounts.length &&
            codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
            data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
              return discount.code === discountCodeValue && discount.applicable === true;
            })
          ) {
            discountCode.value = '';
            await this.#publishCartUpdate(data);
            this.#handleDiscountError('shipping');
            return;
          }
        }

        await this.#publishCartUpdate(data);
      } catch (error) {
        if (error?.cartMutationUncertain !== false) window.BionoboCartMutations?.requireFresh?.();
      } finally {
        this.#activeFetch = null;
        cartPerformance.measureFromEvent('discount-update:user-action', event);
      }
    };
    return window.BionoboCartMutations?.enqueue?.(request) || request();
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const request = async () => {
      await window.BionoboCartMutations?.prepareMutation?.();
      const existingDiscounts = this.#existingDiscounts();
      const index = existingDiscounts.indexOf(discountCode);
      if (index === -1) return;

      existingDiscounts.splice(index, 1);

      const abortController = this.#createAbortController();

      try {
        const config = fetchConfig('json', {
          body: JSON.stringify({ discount: existingDiscounts.join(','),
            sections: window.BionoboCartMutations?.createSectionRequest?.().ids || [this.dataset.sectionId],
            sections_url: window.location.pathname }),
        });

        const response = await fetch(Theme.routes.cart_update_url, {
          ...config,
          signal: abortController.signal,
        });

        let data = await response.json();
        if (!response.ok || data.status || data.errors) {
          const error = new Error(data.description || data.message || 'The discount could not be updated.');
          error.cartMutationUncertain = response.status !== 422;
          throw error;
        }
        data = await this.#reconcileCartUpdate(data);

        await this.#publishCartUpdate(data);
      } catch (error) {
        if (error?.cartMutationUncertain !== false) window.BionoboCartMutations?.requireFresh?.();
      } finally {
        this.#activeFetch = null;
      }
    };
    return window.BionoboCartMutations?.enqueue?.(request) || request();
  };

  async #reconcileCartUpdate(data) {
    const api = window.BionoboCartMutations;
    api?.invalidateRead?.(data);
    const sectionRequest = api?.createSectionRequest?.();
    data = (await sectionRequest?.settle(data)) || data;
    return (await api?.reconcile?.(data)) || data;
  }

  async #publishCartUpdate(data) {
    const api = window.BionoboCartMutations;
    if (api?.publish) {
      await api.publish(data, { source: 'cart-discount', sourceId: this.id, mutation: 'update' });
    } else {
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      await morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    }
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const currentComponent = document.querySelector(`cart-discount-component[data-section-id="${this.dataset.sectionId}"]`) || this;
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = currentComponent.refs || this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
